import { Box, TableCell, type TableCellProps, TableRow } from "@mui/material";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

const skeletonWidths = ["62%", "47%", "71%", "55%", "39%", "66%"];
const resultsEnterClass = "results-enter";
const tableResultsEnterDurationMs = 900;

interface TableSkeletonRowsProps {
  columnCount: number;
  rowCount?: number;
  alignments?: readonly TableCellProps["align"][];
  imageColumns?: readonly number[];
  imageSize?: number;
}

/** Shared lightweight, animated placeholders for data tables. */
export const TableSkeletonRows: React.FC<TableSkeletonRowsProps> = ({
  columnCount,
  rowCount = 5,
  alignments,
  imageColumns = [],
  imageSize = 48,
}) => {
  const [rowOrder, setRowOrder] = useState(() =>
    Array.from({ length: rowCount }, (_, index) => index),
  );
  const [visibleRows, setVisibleRows] = useState<ReadonlySet<number>>(() => new Set());
  const rowRefs = useRef(new Map<number, HTMLTableRowElement>());
  const previousPositions = useRef(new Map<number, number>());

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mediaQuery.matches) {
      setVisibleRows(new Set(Array.from({ length: rowCount }, (_, index) => index)));
      return;
    }

    setVisibleRows(new Set());
    const timers = Array.from({ length: rowCount }, (_, index) =>
      window.setTimeout(
        () => {
          setVisibleRows((currentRows) => new Set([...currentRows, index]));
        },
        180 + index * 200,
      ),
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [rowCount]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mediaQuery.matches || rowCount < 2) return;

    const timer = window.setInterval(() => {
      previousPositions.current = new Map(
        Array.from(rowRefs.current.entries(), ([rowId, row]) => [
          rowId,
          row.getBoundingClientRect().top,
        ]),
      );
      setRowOrder((currentOrder) => {
        const nextOrder = [...currentOrder];
        const firstIndex = Math.floor(Math.random() * (nextOrder.length - 1));
        [nextOrder[firstIndex], nextOrder[firstIndex + 1]] = [
          nextOrder[firstIndex + 1],
          nextOrder[firstIndex],
        ];
        return nextOrder;
      });
    }, 3_600);

    return () => window.clearInterval(timer);
  }, [rowCount]);

  useLayoutEffect(() => {
    if (previousPositions.current.size === 0) return;

    const animationFrame = window.requestAnimationFrame(() => {
      rowOrder.forEach((rowId) => {
        const row = rowRefs.current.get(rowId);
        const previousTop = previousPositions.current.get(rowId);
        if (!row || previousTop === undefined) return;

        const offset = previousTop - row.getBoundingClientRect().top;
        if (offset === 0) return;

        row.style.transition = "none";
        row.style.transform = `translateY(${offset}px)`;
        window.requestAnimationFrame(() => {
          row.style.transition = "transform 760ms cubic-bezier(0.2, 0.8, 0.2, 1)";
          row.style.transform = "translateY(0)";
        });
      });
      previousPositions.current.clear();
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [rowOrder]);

  useLayoutEffect(() => {
    const firstRow = rowRefs.current.values().next().value;
    const tableBody = firstRow?.closest("tbody");
    if (!tableBody) return;

    return () => {
      tableBody.classList.add(resultsEnterClass);
      window.setTimeout(
        () => tableBody.classList.remove(resultsEnterClass),
        tableResultsEnterDurationMs,
      );
    };
  }, []);

  return (
    <>
      {rowOrder.map((rowId) => (
        <TableRow
          className="table-empty-state table-skeleton-row"
          key={rowId}
          ref={(element: HTMLTableRowElement | null) => {
            if (element) rowRefs.current.set(rowId, element);
            else rowRefs.current.delete(rowId);
          }}
          sx={{
            height: 64,
            opacity: visibleRows.has(rowId) ? 1 : 0,
            transition: "opacity 500ms ease-out",
            willChange: "opacity, transform",
            backgroundColor: "#fff !important",
            "& > .MuiTableCell-root": {
              py: 1,
              borderColor: "#f0f3f8",
              backgroundColor: "#fff !important",
            },
          }}
        >
          {Array.from({ length: columnCount }, (_, columnIndex) => {
            const align = alignments?.[columnIndex];
            const isImageColumn = imageColumns.includes(columnIndex);
            const width = skeletonWidths[(rowId + columnIndex) % skeletonWidths.length];
            return (
              <TableCell align={align} key={columnIndex}>
                <Box
                  aria-hidden="true"
                  sx={{
                    width: isImageColumn ? imageSize : width,
                    height: isImageColumn ? imageSize : 12,
                    marginInlineStart: align === "right" ? "auto" : undefined,
                    borderRadius: isImageColumn ? 1 : 999,
                    backgroundColor: "#eef2f7",
                  }}
                />
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </>
  );
};
